import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ordersApi } from "../../services/orders";
import type { TrendingData } from "../../types/orders";
import { parseTrending } from "../DJLeaderboard";
import DriftRow from "../DriftRow";
import { absMediaUrl } from "../../utils/mediaUrl";
import { t } from "../../i18n";
import { T } from "../../i18n/T";
import "./TopSongsCarousel.css";

// The homepage's "Top Songs" row (1.5.2 song-carousel, founder pick B made
// Spotify-safe, 10/23): right under "Trusted by Industry Leaders", the most
// requested songs of the public leaderboard (all time, the board's own
// default), drifting the OTHER way at the talent row's speed.
//
// The covers are Spotify's, shown the way Spotify's design guidelines ask:
// square and uncropped with lightly rounded corners, nothing drawn on them,
// each one linking back to the track on Spotify, and the content credited
// with the Spotify mark ("from [icon] Spotify", under the heading). Request
// counts live on the full board. Song names, artists and "See all" open
// /fan/leaderboard, which a signed-out visitor sees in full. Nothing to show
// (empty board, server hiccup) → no section at all, never an empty row.
const SHOWN = 10;
// Fewer songs than this would loop the same two or three covers: they stand still, centred.
const MIN_LOOP = 4;
const LEADERBOARD = "/fan/leaderboard";

// Spotify's icon (the official glyph, as the playlist page uses it).
const SpotifyIcon: React.FC = () => (
  <svg className="tsc-from-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"
    />
  </svg>
);

const NoteIcon: React.FC = () => (
  <svg className="tsc-cover-fallback-note" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
  </svg>
);

/** "Top Songs", centred like every section heading, with the Spotify credit centred under it. */
const Heading: React.FC = () => (
  <div className="tsc-head">
    <h2 className="section-heading">
      <T k="home.topSongs" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
    </h2>
    <p className="tsc-from">
      <T
        k="home.fromSpotify"
        tags={{
          0: (c) => (
            <span className="tsc-from-brand">
              <SpotifyIcon />
              <b>{c}</b>
            </span>
          ),
        }}
      />
    </p>
  </div>
);

const TopSongsCarousel: React.FC = () => {
  const [songs, setSongs] = useState<TrendingData["songs"] | null>(null);

  useEffect(() => {
    let cancelled = false;
    ordersApi
      .trending("all")
      .then((res) => {
        if (!cancelled) setSongs(parseTrending(res).songs.slice(0, SHOWN));
      })
      .catch(() => {
        if (!cancelled) setSongs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!songs || songs.length === 0) return null;

  return (
    <section className="top-songs-section">
      <Heading />
      <DriftRow
        className="top-songs-row"
        count={songs.length}
        direction={-1}
        minLoop={MIN_LOOP}
        renderSet={(copy, isRepeat) =>
          songs.map((s, i) => {
            const artists = s.artists.join(", ");
            const art = absMediaUrl(s.image);
            const hidden = isRepeat ? ({ tabIndex: -1 } as const) : {};
            const cover = art ? (
              <img className="tsc-cover-img" src={art} alt="" loading="lazy" draggable={false} />
            ) : (
              <span className="tsc-cover-fallback">
                <NoteIcon />
              </span>
            );
            return (
              <div key={`${s.query}-${i}-${copy}`} className="ambassador-card tsc-card" aria-hidden={isRepeat || undefined}>
                {s.trackId ? (
                  <a
                    className="tsc-cover"
                    href={`https://open.spotify.com/track/${s.trackId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("dJLeaderboard.openOnSpotify", { query: s.query })}
                    draggable={false}
                    {...hidden}
                  >
                    {cover}
                  </a>
                ) : (
                  // No Spotify track to link back to: the cover opens the board
                  // like the name below it (one tab stop per card: the name's).
                  <Link className="tsc-cover" to={LEADERBOARD} tabIndex={-1} aria-hidden="true" draggable={false}>
                    {cover}
                  </Link>
                )}
                <Link className="tsc-text" to={LEADERBOARD} title={[s.query, artists].filter(Boolean).join(" · ")} draggable={false} {...hidden}>
                  <span className="ambassador-name tsc-song">{s.query}</span>
                  {artists && <span className="tsc-artist">{artists}</span>}
                </Link>
              </div>
            );
          })
        }
      />
      <Link to={LEADERBOARD} className="tsc-all">
        {t("home.seeAll")}
        <span aria-hidden="true">→</span>
      </Link>
    </section>
  );
};

export default TopSongsCarousel;
